import React, { useLayoutEffect } from 'react';
import cx from 'classnames';
import { DesktopNavigation, MobileNavigation, useNavigationData } from '../components/Navigation';
import Header from '../components/Header';
import { PageNavigationItem } from '../components/PageNavigation';
import { Link } from 'gatsby';
import { getBreadcrumbTrail } from '../utils/get-breadcrumb';
import { BreadcrumbSeparator } from '../components/BreadcrumbSeparator';
import { IduraBanner } from '../components/IduraBanner';

function upperFirst(input: string) {
  if (!input) return input;
  return input.substr(0, 1).toUpperCase() + input.substr(1);
}

export default function DefaultLayout(props: {
  children: React.ReactNode;
  location: Location;
  pageContext: any;
  pageNavigationItems?: PageNavigationItem[];
  isIduraBannerVisible?: boolean;
  onCloseBanner?: () => void;
}) {
  const { frontmatter } = props.pageContext;
  const description = frontmatter?.description || frontmatter?.subtitle;
  const suffix =
    frontmatter?.title === 'Idura Documentation'
      ? ''
      : frontmatter?.product
        ? ` - Idura ${upperFirst(frontmatter.product)} Documentation`
        : ' - Idura Documentation';

  const isEmbedded = props.pageContext?.isEmbedded;
  const category = frontmatter?.category ? ` - ${frontmatter.category}` : '';
  const title = frontmatter?.title + category + suffix;

  useLayoutEffect(() => {
    if (!isEmbedded) return;
    if (!window || !window.parent) return;

    window.parent.postMessage(`height:${document.body.offsetHeight}`, '*');
  }, [isEmbedded]);

  const navData = useNavigationData();
  const breadcrumb = getBreadcrumbTrail(props.location.pathname, frontmatter, navData);

  return (
    <>
      {/** todo: fix, use Gatsby replacement for helmet */}
      <head>
        <meta charSet="utf-8" />
        {title && <title>{title}</title>}
        <link rel="canonical" href={`https://docs.idura.app${props.location.pathname}`} />
        {description && <meta name="description" content={description} />}
      </head>
      <div>
        {!isEmbedded ? (
          <>
            <Header path={props.location.pathname} />
            <MobileNavigation
              key="mobileNav"
              path={props.location.pathname}
              frontmatter={frontmatter}
              pageNavigationItems={props.pageNavigationItems}
            />
          </>
        ) : null}
        <div
          className={cx({
            'px-4 sm:px-6 md:px-8 mx-auto w-full max-w-screen-2xl': !isEmbedded,
          })}
          key="content"
        >
          {/* Outer centered container */}
          <div
            className={cx({
              'pt-5 lg:pt-10': !isEmbedded,
            })}
          >
            {/* Layout grid:
                - mobile: single column
                - lg: 2 columns (22rem nav + flexible content)
                - xl: 3 columns (22rem nav + flexible content + 22rem right column)
            */}
            <div
              className={cx({
                'lg:grid lg:grid-cols-[22rem_minmax(0,1fr)] lg:gap-x-0 xl:grid-cols-[22rem_minmax(0,1fr)_22rem]':
                  !isEmbedded,
              })}
            >
              {/* Left column: Desktop navigation */}
              {!isEmbedded ? (
                <div className="hidden lg:block">
                  <DesktopNavigation
                    key="desktopNav"
                    path={props.location.pathname}
                  />
                </div>
              ) : null}

              {/* Middle column: Main content */}
              <div>
                <a id="overview" />
                <IduraBanner
                  onClose={props.onCloseBanner}
                  isVisible={props.isIduraBannerVisible}
                  className="xl:hidden"
                />
                {frontmatter && (
                  <header id="header" className="relative z-20 mb-8">
                    <div>
                      {!isEmbedded && (frontmatter.category || frontmatter.subcategory) && (
                        <p className="hidden lg:inline-flex items-center mb-2 text-lg leading-6 font-sans font-semibold text-light-blue-600 uppercase ">
                          {frontmatter.category}
                          {breadcrumb
                            ? breadcrumb.map((crumb, index) => (
                                <React.Fragment key={index}>
                                  {crumb.href ? (
                                    <Link to={crumb.href} className="hover:underline">
                                      {crumb.label}
                                    </Link>
                                  ) : (
                                    <p>{crumb.label}</p>
                                  )}
                                  <BreadcrumbSeparator />
                                </React.Fragment>
                              ))
                            : null}
                          {frontmatter.subcategory && (
                            <>
                              <span className="hidden lg:block">{frontmatter.subcategory}</span>
                            </>
                          )}
                        </p>
                      )}
                      <h1 className="block text-display-xl font-medium text-light-blue-900 tracking-tight break-words">
                        {frontmatter.title}
                      </h1>
                    </div>
                    {!isEmbedded && frontmatter.subtitle && (
                      <p className="mt-2 text-lg text-light-blue-800 max-w-screen-sm">
                        {frontmatter.subtitle}
                      </p>
                    )}
                  </header>
                )}
                {props.children}
              </div>

              {/* Right column: reserved space (22rem) for xl screens.*/}
              <div className="hidden xl:block" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
